/**
 * ChatWindow Component
 * Main conversation area for chatbot interaction.
 */

import { useEffect, useRef } from 'react';
import { CHATBOT_STRINGS } from '../../constants';
import { BotAvatarIcon, UserIcon } from '../Icons';
import type { ChatMessage } from '../../types';
import styles from './ChatWindow.module.css';

/**
 * ChatWindow component props.
 */
interface ChatWindowProps {
  /** List of chat messages to display */
  messages?: ChatMessage[];
  /** Chatbot system name */
  systemName?: string;
  /** Whether to show online status indicator */
  showStatus?: boolean;
  /** Compact mode for floating widget */
  compact?: boolean;
  /** Whether the AI is processing a response */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Chat window component.
 * Displays chatbot header with system name and online status indicator.
 * Renders conversation messages with auto-scroll.
 */
export const ChatWindow = ({ 
  messages = [], 
  systemName = CHATBOT_STRINGS.SYSTEM_NAME,
  showStatus = true,
  compact = false,
  isLoading = false,
  error = null,
}: ChatWindowProps) => {
  const hasMessages = messages.length > 0;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isLoading]);

  return (
    <div className={`${styles.chatWindow} ${compact ? styles.compact : ''}`}>
      {/* Chatbot Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.botIcon}>
            <BotAvatarIcon size={24} />
          </div>
          <div className={styles.headerText}>
            <div className={styles.systemName}>{systemName}</div>
            {showStatus && (
              <div className={styles.status}>
                <span className={styles.statusDot}></span>
                <span className={styles.statusText}>{CHATBOT_STRINGS.STATUS_ONLINE}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div 
        className={styles.messagesArea}
        role="log"
        aria-live="polite"
        aria-label={CHATBOT_STRINGS.ARIA_CHAT_MESSAGES}
      >
        {!hasMessages && !isLoading ? (
          <div className={styles.welcomeMessage}>
            <div className={styles.messageBubble}>
              <div className={styles.messageIcon}>
                <BotAvatarIcon size={20} />
              </div>
              <div className={styles.messageContent}>{CHATBOT_STRINGS.WELCOME_MESSAGE}</div>
            </div>
          </div>
        ) : (
          <div className={styles.messagesList}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`${styles.messageBubble} ${
                  message.senderType === 'user' ? styles.userMessage : styles.assistantMessage
                }`}
              >
                <div className={styles.messageIcon}>
                  {message.senderType === 'user' ? (
                    <UserIcon size={20} />
                  ) : (
                    <BotAvatarIcon size={20} />
                  )}
                </div>
                <div className={styles.messageBody}>
                  <div
                    className={`${styles.messageContent} ${
                      message.senderType === 'user' ? styles.userContent : ''
                    }`}
                  >
                    {message.content}
                  </div>
                  {/* Source references for AI responses */}
                  {message.senderType === 'assistant' &&
                    message.sources &&
                    message.sources.length > 0 && (
                      <div className={styles.sourcesContainer}>
                        <span className={styles.sourcesLabel}>Sources:</span>
                        {message.sources.map((source, idx) => (
                          <span key={idx} className={styles.sourceTag}>
                            {source.title}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className={`${styles.messageBubble} ${styles.assistantMessage}`}>
                <div className={styles.messageIcon}>
                  <BotAvatarIcon size={20} />
                </div>
                <div className={styles.messageContent}>
                  <div className={styles.typingIndicator}>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className={styles.errorMessage}>
                <span>{error}</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};
